import equal from 'fast-deep-equal';
import moment, { Moment } from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { useLeaflet } from 'react-leaflet';
import { Capability } from '../../../services/hooks/useCapabilities';
import { PICASSO_SERVER, WMSServer } from '../MapConfig';
import { HideableWMSTileLayer } from './HideableWMSTileLayer';
import { useBoundsAndZoom } from '../../../services/hooks/useBoundsAndZoom';

const momentToIsoNoMillis = (val: Moment) =>
  val.toISOString().split('.')[0] + 'Z';

export interface TimedLayerProps {
  capability: Capability;
  server: WMSServer;
  desiredTime: string;
  zIndex: number;
}

// calculates the n closest times to supplied value, if there are fewer
// than n items it will return all the items
const closestTimes = (
  times: string[],
  momentTimes: Moment[],
  time: string,
  n: number
): string[] => {
  const x = moment(time);
  const deltaMap = momentTimes.map((y, i) => ({
    delta: Math.abs(x.diff(y)),
    index: i,
  }));
  deltaMap.sort((a, b) => a.delta - b.delta);
  return deltaMap.slice(0, n).map((elem) => times[elem.index]);
};

interface Cache {
  [key: string]: boolean;
}

function arrayToCache(vals: string[] | undefined): Cache {
  const ret: Cache = {};
  vals?.forEach((val) => (ret[val] = true));
  return ret;
}

function cacheToArray(c: Cache): string[] {
  return Object.keys(c).filter((key) => c[key]);
}

export const TimedLayer: React.FunctionComponent<TimedLayerProps> = ({
  capability,
  server,
  desiredTime,
  zIndex,
}) => {
  const { layerId, refTime, times: momentTimes } = capability;
  const { map } = useLeaflet();
  server = server || PICASSO_SERVER;

  const times = useMemo<string[] | undefined>(
    () => momentTimes && momentTimes.map((t) => momentToIsoNoMillis(t)),
    [momentTimes]
  );
  const timesToUse = useMemo<string[] | undefined>(() => {
    return (
      times && momentTimes && closestTimes(times, momentTimes, desiredTime, 3)
    );
  }, [times, momentTimes, desiredTime]);

  const [cache, setCache] = useState<Cache>(arrayToCache(timesToUse));

  // update list of already loaded times
  useEffect(() => {
    const newCache = arrayToCache(cacheToArray(cache).concat(timesToUse || []));
    if (!equal(newCache, cache)) setCache(newCache);
  }, [timesToUse?.join(',')]);

  const { bounds, zoom } = useBoundsAndZoom(map);
  // if we move around in the map discard the list of already loaded times
  useEffect(() => {
    const newCache = arrayToCache(timesToUse);
    if (!equal(newCache, cache)) setCache(newCache);
  }, [bounds?.toBBoxString(), zoom]);

  const wmsProps = {
    url: server.url,
    layers: layerId,
    format: 'image/png',
    tileSize: 512,
    transparent: true,
  };

  if (refTime) {
    wmsProps['dim_reftime'] = refTime;
  }

  if (server.subdomains) {
    wmsProps['subdomains'] = server.subdomains;
  }

  // Renders multiple tile layers to reduce loading times, hides the
  // layers that do not correspond to the desired time.
  return (
    <>
      {cacheToArray(cache).map((time) => (
        <HideableWMSTileLayer
          key={time}
          {...wmsProps}
          time={time}
          zIndex={zIndex}
          visible={time === desiredTime}
        />
      ))}
    </>
  );
};
